import React from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mui/material';

export function createBaseRegistry(ctx = {}) {
    const {
        contacts = {},
        loans = {},
        leadopps = {},
        formatPhone,
        formatName,
        singleSelectQuickFilter,
    } = ctx;

    const getContact = (cid) => (cid ? contacts[cid] : null);

    const getBestPhone = (contact) => {
        if (!contact) return null;
        return contact.phoneMobile || contact.phone || contact.phoneWork || contact.phoneHome || null;
    };

    const safeDate = (value) => {
        if (!value) return null;
        const d = new Date( value.match( 'T' ) ? value : `${value}T12:00:00` );
        return Number.isNaN(d.getTime()) ? null : d;
    };

    const resolveSourceValue = ({ row, colDef, value }) => {
        const source = colDef?.helperOptions?.source;
        if (!source) return value;

        if (typeof source === 'function') {
            return source({ row, value, colDef });
        }

        return row?.[source];
    };

    return {
        text: {
            valueFormatter: ({ value }) => value ?? '',
        },

        date: {
            valueGetter: ({ value, colDef, row }) => {
                const raw = resolveSourceValue({ row, colDef, value });
                const d = safeDate(raw);
                if (!d) return null;
                return d;
            },
            renderCell: ({ value, colDef }) => {
                if (!(value instanceof Date)) return null;

                const formatter = colDef?.helperOptions?.dateFormatter;
                return formatter ? formatter(value) : value.toLocaleDateString();
            },
            valueFormatter: ({ value, colDef }) => {
                if (!(value instanceof Date)) return null;

                const formatter = colDef?.helperOptions?.dateFormatter;
                return formatter ? formatter(value) : value.toLocaleDateString();
            },
        },

        phone: {
            renderCell: ({ value }) =>
                value ? <a href={`tel:${value}`}>{value}</a> : null,
            valueFormatter: ({ value }) => value || null,
        },

        contactPhone: {
            valueGetter: ({ row, colDef, value }) => {
                const cid = resolveSourceValue({ row, colDef, value });
                const contact = getContact(cid);
                const phone = getBestPhone(contact);

                return phone && formatPhone ? formatPhone(phone) : phone || null;
            },
            renderCell: ({ value }) =>
                value ? <a href={`tel:${value}`}>{value}</a> : null,
            valueFormatter: ({ value }) => value || null,
        },

        email: {
            renderCell: ({ value }) =>
                value ? <a href={`mailto:${value}`}>{value}</a> : null,
            valueFormatter: ({ value }) => value || null,
        },

        contactEmail: {
            valueGetter: ({ row, colDef, value }) => {
                const cid = resolveSourceValue({ row, colDef, value });
                const contact = getContact(cid);
                return contact?.email || null;
            },
            renderCell: ({ value }) =>
                value ? <a href={`mailto:${value}`}>{value}</a> : null,
            valueFormatter: ({ value }) => value || null,
        },

        contact: {
            renderCell: ({ row, colDef, value }) => {
                const cid = resolveSourceValue({ row, colDef, value });
                const contact = getContact(cid);
                console.log( 'render contact cid:', cid, contact );
                if (!contact) return null;

                const label =
                    (formatName && formatName(contact.cid)) ||
                    contact.name ||
                    contact.cid;

                return <Link to={`/contact/${contact.cid}`}>{label}</Link>;
            },
            valueFormatter: ({ row, colDef, value }) => {
                const cid = resolveSourceValue({ row, colDef, value });
                const contact = getContact(cid);
                if (!contact) return null;

                return (
                    (formatName && formatName(contact.cid)) ||
                    contact.name ||
                    contact.cid
                );
            },
        },

        link: {
            renderCell: ({ row, colDef, value }) => {
                const { getHref, hrefTemplate, labelField, fallbackToValue = true } =
                    colDef?.helperOptions || {};

                let href = null;

                if (typeof getHref === 'function') {
                    href = getHref({ row, value, colDef });
                } else if (hrefTemplate) {
                    href = hrefTemplate.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => row?.[key] ?? '');
                }

                const label = labelField ? row?.[labelField] : value;

                if (!href) return fallbackToValue ? (label ?? null) : null;
                return <Link to={href}>{label}</Link>;
            },
        },

        html: {
            renderCell: ({ value }) => {
                if (!value) return null;

                return (
                    <Box
                        component="div"
                        sx={{
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            lineHeight: 1.4,
                            display: 'block',
                        }}
                        dangerouslySetInnerHTML={{ __html: value }}
                    />
                );
            },
        },

        number: {
            valueFormatter: ({ value, colDef }) => {
                if (typeof value !== 'number') return value;

                const options = colDef?.helperOptions?.numberFormatOptions;
                return value.toLocaleString(undefined, options);
            },
        },

        rate: {
            valueGetter: ({ value, row, colDef }) => {
                const raw = resolveSourceValue({ row, colDef, value });
                if (raw == null || raw === '') return null;

                const cleaned = raw.toString().replace(/[^0-9.%]+/g, '');
                if (!cleaned) return null;

                if (cleaned.includes('%')) {
                    return parseFloat(cleaned.replace('%', ''));
                }

                return parseFloat(cleaned);
            },
            valueFormatter: ({ value, colDef }) => {
                if (typeof value !== 'number') return value;

                const decimals = colDef?.helperOptions?.decimals ?? 3;
                const suffix = colDef?.helperOptions?.suffix ?? '%';

                return `${value.toFixed(decimals)}${suffix}`;
            },
        },

        singleSelectQuickFilter: {
            getApplyQuickFilterFn: singleSelectQuickFilter,
        },
    };
}